import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FileText, Sparkles, Zap, Loader2, Download, Copy, Check, LogOut,
  Target, Briefcase, Mail, FileSignature, AlertTriangle, RefreshCw, ChevronRight,
  Rocket, ChevronDown, ChevronUp, MapPin, Lock,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useFeatures } from "../lib/features";
import { ENGINES, ENGINE_KEYS_FULL, MODEL_LOCK_MESSAGE } from "../lib/models";
import { downloadCoverLetterPdf } from "../lib/coverLetterPdf";
import { WaitingUX } from "../components/WaitingUX";
import { useJobProgress } from "../lib/useJobProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMP = [
  { v: "contract-c2c", label: "Contract C2C" },
  { v: "full-time", label: "Full-Time" },
];

const BUCKET_COLOR = {
  green: "#10b981", strengthen: "#10b981",
  yellow: "#f59e0b", enhance: "#f59e0b",
  red: "#ef4444", guard: "#ef4444",
};

const STEP_LABEL = {
  optimizing: "Rewriting your resume…",
  scoring: "Scoring the match…",
  generating: "Finalizing document…",
  email: "Composing recruiter email…",
  cover_letter: "Writing cover letter…",
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function pollJob(url, { onStep } = {}) {
  const start = Date.now();
  while (Date.now() - start < 300000) {
    await sleep(1500);
    const { data } = await axios.get(url);
    if (onStep && data.step) onStep(data.step);
    if (data.status === "completed") return data.result ?? data;
    if (data.status === "failed") throw new Error(data.error || "Processing failed");
  }
  throw new Error("Timed out. Please try again.");
}

const asText = (r, ...keys) => {
  if (typeof r === "string") return r;
  for (const k of keys) if (r && r[k]) return r[k];
  return "";
};

/* ── Credit pill ── */
function CreditPill({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-muted rounded-full border border-border/50" data-testid={`ext-credit-${label.toLowerCase()}`}>
      <Icon size={11} className="text-primary" />
      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-xs font-bold text-foreground leading-none">{value}</span>
    </div>
  );
}

/* ── Login ── */
function LoginCard() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(email.trim(), password); }
    catch (e2) { setErr(e2.response?.data?.detail || "Login failed. Check your credentials."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col items-center justify-center min-h-[420px] gap-5 text-center" data-testid="ext-login-form">
      <div>
        <div className="inline-flex items-center gap-2 text-foreground font-semibold text-xl tracking-tight">
          <span className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30"><FileText size={17} className="text-white" /></span>
          ResumeAI
        </div>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">Scan, tailor &amp; apply<br />on any job page — in one panel.</p>
      </div>
      <div className="w-full space-y-3">
        <input type="email" required placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-sm text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-login-email" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-sm text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-login-password" />
        {err && <p className="text-xs text-red-500 text-left" data-testid="ext-login-error">{err}</p>}
        <button type="submit" disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="ext-login-submit">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ChevronRight size={15} />} Sign In
        </button>
      </div>
      <a href="/signup" target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground transition-colors">No account? Start free →</a>
    </form>
  );
}

/* ── Score ring ── */
function ScoreRing({ pct }) {
  const c = 2 * Math.PI * 32;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * c;
  const color = pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative shrink-0" style={{ width: 84, height: 84 }}>
      <svg width="84" height="84" viewBox="0 0 84 84">
        <circle cx="42" cy="42" r="32" fill="none" stroke="var(--border)" strokeWidth="7" />
        <circle cx="42" cy="42" r="32" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`} transform="rotate(-90 42 42)" style={{ transition: "stroke-dasharray 1s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold font-mono leading-none" style={{ color }} data-testid="ext-scan-score">{Math.round(pct)}<span className="text-sm">%</span></span>
        <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">match</span>
      </div>
    </div>
  );
}

/* ── Tickable skill row (consent) ── */
function SkillCheck({ action, checked, onToggle, placement, onPlacement }) {
  const color = BUCKET_COLOR[action.bucket] || "#6366f1";
  return (
    <div className={`rounded-lg border p-2.5 transition-colors ${checked ? "bg-primary/5 border-primary/40" : "bg-background border-border/50"}`} data-testid={`ext-skill-${action.skill}`}>
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onToggle} className="mt-0.5 accent-primary shrink-0" data-testid={`ext-skill-check-${action.skill}`} />
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 flex-wrap">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
            <span className="text-sm font-semibold text-foreground">{action.skill}</span>
            {action.must_have && <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-foreground/10 text-foreground">Req</span>}
            {action.section_only && <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-rose-500/15 text-rose-500">Skills section</span>}
          </span>
          {action.detail && <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">{action.detail}</span>}
        </span>
      </label>
      {action.bucket === "red" && checked && (
        <div className="pl-6 mt-1.5 space-y-1" data-testid={`ext-placement-${action.skill}`}>
          {[["section_only", "Skills section only (recommended)"], ["weave", "Also weave into my experience"]].map(([val, lab]) => (
            <label key={val} className="flex items-center gap-1.5 text-[11px] cursor-pointer" data-testid={`ext-placement-${val}-${action.skill}`}>
              <input type="radio" name={`ext-pl-${action.id}`} checked={(placement || "section_only") === val} onChange={() => onPlacement(val)} className="accent-rose-500" />
              <span className={val === "weave" ? "text-rose-500" : "text-muted-foreground"}>{lab}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Result text preview ── */
function TextPreview({ text, testid, serif }) {
  return (
    <pre className={`whitespace-pre-wrap text-xs text-foreground bg-background border border-border rounded-lg p-3 max-h-52 overflow-auto ${serif ? "font-serif" : "font-sans"}`} data-testid={testid}>{text}</pre>
  );
}

export default function ExtensionSidebar() {
  const { user, loading, logout, refreshUser } = useAuth();
  const { features } = useFeatures();
  const isModelLocked = (m) => features?.[ENGINES[m]?.lockKey] === true && user?.role !== "admin";

  const [resumes, setResumes] = useState([]);
  const [resumesReady, setResumesReady] = useState(false);
  const [resumeId, setResumeId] = useState("");
  const [employment, setEmployment] = useState("contract-c2c");
  const [aiModel, setAiModel] = useState("glm-5.2");

  const [jobUrl, setJobUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [platform, setPlatform] = useState("");
  const [jd, setJd] = useState("");
  const [detectedEmail, setDetectedEmail] = useState("");
  const [detecting, setDetecting] = useState(false);

  const [busy, setBusy] = useState("");
  const [step, setStep] = useState("");
  const [error, setError] = useState("");
  const [gate, setGate] = useState("");

  const [scan, setScan] = useState(null);
  const [scanId, setScanId] = useState("");
  const [selectedActions, setSelectedActions] = useState([]);
  const [skillPlacements, setSkillPlacements] = useState({});
  const [jobStep, setJobStep] = useState("");
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [optimized, setOptimized] = useState(null);
  const [cover, setCover] = useState("");
  const [emailText, setEmailText] = useState("");
  const [dlBusy, setDlBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const jdRef = useRef(null);

  const jobActive = busy === "scan" || busy === "optimize";
  const jobProgress = useJobProgress({
    active: jobActive,
    phase: busy === "optimize" ? "optimize" : "scan",
    step: jobStep,
  });

  const u = user?.usage || {};
  const isUnlimited = (user?.subscription?.plan || "trial") === "unlimited";
  const optRemNum = Math.max(0, (u.opt_limit ?? u.scans_limit ?? 0) - (u.opt_used ?? u.scans_used ?? 0) + (u.opt_addon ?? u.addon_scans ?? 0));
  const scanRemNum = Math.max(0, (u.scan_limit ?? u.scans_limit ?? 0) - (u.scan_used ?? 0) + (u.scan_addon ?? 0));
  const optRem = isUnlimited ? "∞" : optRemNum;
  const scanRem = isUnlimited ? "∞" : scanRemNum;
  const walletEmpty = !isUnlimited && (optRemNum <= 0 || scanRemNum <= 0);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/resumes`);
        if (!alive) return;
        setResumes(data || []);
        const def = (data || []).find((r) => r.is_default) || (data || [])[0];
        // Only default the selection when the user hasn't already picked one.
        // (Keyed on user?.id so a post-scan refreshUser() never resets this.)
        if (def) setResumeId((prev) => prev || def.id);
      } catch { /* ignore */ }
      finally { if (alive) setResumesReady(true); }
    })();
    return () => { alive = false; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onMsg(e) {
      const d = e.data;
      if (!d || d.type !== "RESUMEAI_JOB_DATA") return;
      if (d.job_description) setJd(d.job_description);
      if (d.company_name) setCompany(d.company_name);
      if (d.job_title) setRole(d.job_title);
      if (d.platform) setPlatform(d.platform);
      if (Array.isArray(d.emails) && d.emails.length) setDetectedEmail(d.emails[0]);
      if (d.success === false && d.error) setError(d.error);
      setDetecting(false);
    }
    window.addEventListener("message", onMsg);
    try { window.parent?.postMessage({ type: "RESUMEAI_SIDEBAR_READY" }, "*"); } catch { /* ignore */ }
    const q = new URLSearchParams(window.location.search);
    if (q.get("jd")) setJd(q.get("jd"));
    if (q.get("company")) setCompany(q.get("company"));
    if (q.get("role")) setRole(q.get("role"));
    if (q.get("platform")) setPlatform(q.get("platform"));
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const reset = () => { setError(""); setGate(""); };
  const inExtension = window.parent !== window;

  const clearJob = () => { setJd(""); setCompany(""); setRole(""); setPlatform(""); setDetectedEmail(""); };

  const requestDetect = () => {
    reset();
    if (!inExtension) {
      setError("Open the ResumeAI side panel on a job page to detect. Here, paste the job URL or the description text.");
      return;
    }
    setDetecting(true);
    // Clear the previous job first so re-detecting always reflects the job that's
    // CURRENTLY open — switching jobs + Detect now updates instead of keeping stale data.
    clearJob();
    try { window.parent.postMessage({ type: "RESUMEAI_REQUEST_DETECT" }, "*"); } catch { setDetecting(false); }
    setTimeout(() => setDetecting(false), 9000);
  };

  const extractFromUrl = async () => {
    reset();
    if (!jobUrl.trim()) { setError("Paste a job URL first."); return; }
    setExtracting(true);
    clearJob();
    try {
      const { data } = await axios.post(`${API}/jd/extract`, { url: jobUrl.trim() }, { timeout: 25000 });
      if (data.error && !data.job_description) { setError(data.error); return; }
      if (data.job_description) setJd(data.job_description);
      if (data.job_title) setRole(data.job_title);
      if (data.company_name && data.company_name_confidence === "high") setCompany(data.company_name);
      if (data.platform) setPlatform(data.platform);
      if (data.recruiter_email) setDetectedEmail(data.recruiter_email);
      if (!data.job_description) setError("Couldn't extract the job description from that link. Paste the text instead.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to extract from that URL. Paste the description text instead.");
    } finally { setExtracting(false); }
  };

  const needJob = () => {
    if (!resumeId) { setError("Select a resume first."); return true; }
    if (jd.trim().length < 30) { setError("Paste or detect a job description first."); jdRef.current?.focus(); return true; }
    return false;
  };
  const handleErr = (e, action) => {
    const detail = e.response?.data?.detail || e.message || `${action} failed`;
    if (e.response?.status === 403 && /subscription/i.test(detail)) setGate(detail);
    else setError(detail);
  };
  const baseForm = () => {
    const fd = new FormData();
    fd.append("saved_resume_id", resumeId);
    fd.append("job_description", jd);
    fd.append("ai_model", aiModel);
    fd.append("employment_type", employment);
    return fd;
  };

  const runScan = useCallback(async () => {
    reset(); if (needJob()) return;
    setBusy("scan"); setStep("Scanning & analyzing…"); setJobStep(""); setScan(null); setShowAllSkills(false);
    try {
      const fd = baseForm(); fd.append("consume_credit", "1");
      const { data } = await axios.post(`${API}/scan`, fd);
      setScanId(data.scan_id || "");
      const result = await pollJob(`${API}/scan/status/${data.scan_id}`);
      setScan(result);
      // Pre-tick the safe actions the backend recommends; leave the rest for the user.
      setSelectedActions((result.action_plan || []).filter((a) => a.default_checked).map((a) => a.id));
      setSkillPlacements({});
      refreshUser();
    } catch (e) { handleErr(e, "Scan"); }
    finally { setBusy(""); setStep(""); }
  }, [resumeId, jd, aiModel, employment]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAction = (id) =>
    setSelectedActions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const setPlacement = (skill, val) =>
    setSkillPlacements((prev) => ({ ...prev, [skill]: val }));

  const runOptimize = useCallback(async () => {
    reset(); if (needJob()) return;
    setBusy("optimize"); setStep("Optimizing your resume…"); setJobStep("optimizing"); setOptimized(null);
    try {
      const fd = baseForm();
      // Match the website's best-quality pipeline (My Template / clone & fill).
      fd.append("format_mode", "clone_fill");
      fd.append("generate_email", "false");
      fd.append("generate_cover_letter_opt", "false");
      if (company.trim()) fd.append("company_name", company.trim());
      if (role.trim()) fd.append("job_title", role.trim());
      // ── Consent: reuse the scan + only add the skills the user ticked (quality parity
      //    with the website — no scan_id means the backend re-runs analysis and stuffs
      //    every JD skill; passing these makes it faster AND honest). ──
      const plan = scan?.action_plan || [];
      const addSkills = plan
        .filter((a) => a.type === "add_skill" && selectedActions.includes(a.id))
        .map((a) => a.skill);
      fd.append("approved_skills", JSON.stringify(addSkills));
      const placements = {};
      plan
        .filter((a) => a.type === "add_skill" && a.bucket === "red" && selectedActions.includes(a.id))
        .forEach((a) => { placements[a.skill] = skillPlacements[a.skill] || "section_only"; });
      fd.append("skill_placements", JSON.stringify(placements));
      if (scanId) fd.append("scan_id", scanId);
      const { data } = await axios.post(`${API}/optimize`, fd);
      const result = await pollJob(`${API}/optimize/status/${data.job_id}`, { onStep: (s) => s && setJobStep(s) });
      setOptimized(result); refreshUser();
    } catch (e) { handleErr(e, "Optimize"); }
    finally { setBusy(""); setStep(""); setJobStep(""); }
  }, [resumeId, jd, aiModel, employment, company, role, scan, scanId, selectedActions, skillPlacements]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCover = useCallback(async () => {
    reset(); if (needJob()) return;
    setBusy("cover"); setStep("Writing cover letter…"); setCover("");
    try {
      const { data } = await axios.post(`${API}/cover-letter/generate`, {
        saved_resume_id: resumeId, job_description: jd,
        job_title: role, company_name: company, employment_type: employment,
      });
      const result = await pollJob(`${API}/cover-letter/status/${data.job_id}`);
      setCover(asText(result, "cover_letter", "text"));
    } catch (e) { handleErr(e, "Cover letter"); }
    finally { setBusy(""); setStep(""); }
  }, [resumeId, jd, role, company, employment]); // eslint-disable-line react-hooks/exhaustive-deps

  const runEmail = useCallback(async () => {
    reset(); if (needJob()) return;
    setBusy("email"); setStep("Composing recruiter email…"); setEmailText("");
    try {
      const fd = new FormData();
      fd.append("saved_resume_id", resumeId);
      fd.append("job_description", jd);
      if (company.trim()) fd.append("company_name", company.trim());
      fd.append("employment_type", employment);
      fd.append("email_length", "short");
      const { data } = await axios.post(`${API}/write-email`, fd);
      const result = await pollJob(`${API}/generate-email/status/${data.job_id}`);
      setEmailText(asText(result, "email_body", "email", "text"));
    } catch (e) { handleErr(e, "Recruiter email"); }
    finally { setBusy(""); setStep(""); }
  }, [resumeId, jd, company, employment]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCover = async () => {
    setDlBusy(true);
    try { await downloadCoverLetterPdf(cover, { companyName: company, jobTitle: role }); }
    finally { setDlBusy(false); }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(emailText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  };

  const openGmail = () => {
    const lines = emailText.split("\n");
    let subject = `Application${role ? `: ${role}` : ""}${company ? ` — ${company}` : ""}`;
    let body = emailText;
    const subjLine = lines.find((l) => l.toLowerCase().startsWith("subject:"));
    if (subjLine) {
      subject = subjLine.replace(/^subject:\s*/i, "").trim();
      body = lines.filter((l) => !l.toLowerCase().startsWith("subject:")).join("\n").trim();
    }
    // "To": prefer the scraped page email, else any recruiter email mentioned in the JD.
    const findEmail = (t) => (t.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/) || [])[0] || "";
    const to = (detectedEmail || findEmail(jd)).trim();
    const url = `https://mail.google.com/mail/?view=cm&fs=1${to ? `&to=${encodeURIComponent(to)}` : ""}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 size={26} className="animate-spin text-primary" /></div>;
  }

  const actionsDisabled = !!busy || !resumesReady;

  // Scan derived
  let scanView = null;
  if (scan) {
    const kb = scan.keyword_breakdown || {};
    const pct = kb.match_score ?? scan.match_score ?? 0;
    const all = [...(kb.groups?.technical || []), ...(kb.groups?.soft || []), ...(kb.groups?.domain || [])];
    const matched = all.filter((s) => s.state === "matched").length;
    const partial = all.filter((s) => s.state === "partial").length;
    const missing = all.filter((s) => s.state === "missing").length;
    const gaps = (scan.action_plan || []).filter((a) => a.type === "add_skill");
    scanView = { pct, matched, partial, missing, gaps };
  }

  return (
    <div className="w-full max-w-[420px] mx-auto min-h-screen bg-background text-foreground flex flex-col" data-testid="extension-sidebar">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 flex items-center justify-between px-4 py-3 bg-background/90 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center gap-2 font-semibold text-base">
          <span className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center shadow-lg shadow-primary/25"><FileText size={15} className="text-white" /></span>
          ResumeAI
        </div>
        {user && (
          <div className="flex items-center gap-2">
            <CreditPill icon={Sparkles} label="Opt" value={optRem} />
            <CreditPill icon={Target} label="Scan" value={scanRem} />
            <button onClick={logout} title="Sign out" className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors" data-testid="ext-logout"><LogOut size={14} /></button>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5 pb-10">
        {!user ? (
          <LoginCard />
        ) : (
          <>
            {walletEmpty && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10" data-testid="ext-lowcredit">
                <span className="text-xs text-amber-500 font-medium">{optRemNum <= 0 ? "Out of optimizations" : "Out of scans"}. Top up to keep going.</span>
                <a href="/app/pricing" target="_blank" rel="noreferrer" className="text-[10px] uppercase font-bold text-amber-500 border border-amber-500 px-2 py-1 rounded whitespace-nowrap" data-testid="ext-topup-btn">Top up</a>
              </div>
            )}

            {/* Job details */}
            <div className="p-4 bg-card rounded-xl border border-border space-y-3 relative" data-testid="ext-job-context">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Job Details</span>
                {platform && <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-primary/10 text-primary border border-primary/20" data-testid="ext-platform">{platform}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Company" value={company} onChange={(e) => setCompany(e.target.value)} className="px-2.5 py-2 rounded-md border border-border bg-background text-xs focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-company" />
                <input placeholder="Role" value={role} onChange={(e) => setRole(e.target.value)} className="px-2.5 py-2 rounded-md border border-border bg-background text-xs focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-role" />
              </div>
              <div className="flex gap-2" data-testid="ext-url-row">
                <input value={jobUrl} onChange={(e) => setJobUrl(e.target.value)}
                  placeholder="Paste a job URL (LinkedIn, Dice, Greenhouse…)"
                  className="flex-1 px-2.5 py-2 rounded-md border border-border bg-background text-xs focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-jd-url" />
                <button onClick={extractFromUrl} disabled={extracting || !jobUrl.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 shrink-0 transition-opacity" data-testid="ext-jd-extract-btn">
                  {extracting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Extract
                </button>
              </div>
              <textarea ref={jdRef} placeholder="…or paste the job description text here. On a job page, hit ‘Detect from page’ to auto-fill the open job." value={jd} onChange={(e) => setJd(e.target.value)}
                rows={5} className="w-full px-3 py-2 rounded-md border border-border bg-background text-xs resize-y focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-jd" />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">{jd.trim().length} chars{detectedEmail ? ` · ✉ ${detectedEmail}` : ""}</span>
                <button onClick={requestDetect} disabled={detecting} className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 px-2.5 py-1.5 bg-primary/5 rounded-md border border-primary/10 transition-colors" data-testid="ext-detect-btn">
                  {detecting ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Detect from page
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="p-4 bg-card rounded-xl border border-border space-y-3.5">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">Resume</label>
                <select value={resumeId} onChange={(e) => setResumeId(e.target.value)} className="w-full h-9 px-3 rounded-md border border-border bg-background text-xs focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none" data-testid="ext-resume-select">
                  <option value="">Select a resume…</option>
                  {resumes.map((r) => <option key={r.id} value={r.id}>{r.display_name || r.filename}{r.is_default ? " (default)" : ""}</option>)}
                </select>
                {resumesReady && resumes.length === 0 && <p className="text-[10px] text-amber-500 mt-1">No saved resumes. <a href="/app/resumes" target="_blank" rel="noreferrer" className="underline">Upload one →</a></p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5 flex items-center gap-1"><Briefcase size={10} /> Type</label>
                  <div className="flex gap-1.5">
                    {EMP.map((e) => (
                      <button key={e.v} onClick={() => setEmployment(e.v)} data-testid={`ext-emp-${e.v}`}
                        className={`flex-1 px-1.5 py-1.5 rounded-md border text-[11px] font-medium transition-colors ${employment === e.v ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}>
                        {e.v === "contract-c2c" ? "C2C" : "Full-Time"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5 flex items-center gap-1"><Zap size={10} /> Engine</label>
                  <div className="flex gap-1.5">
                    {ENGINE_KEYS_FULL.map((m) => {
                      const locked = isModelLocked(m);
                      return (
                      <button key={m} type="button"
                        onClick={() => { if (locked) { setError(MODEL_LOCK_MESSAGE); return; } setAiModel(m); }}
                        data-testid={`ext-model-${m === "glm-5.2" ? "glm" : m === "deepseek-v4-pro" ? "deepseek" : "gpt"}`}
                        aria-disabled={locked || undefined}
                        title={locked ? MODEL_LOCK_MESSAGE : ENGINES[m]?.label}
                        className={`flex-1 inline-flex items-center justify-center gap-1 px-1 py-1.5 rounded-md border text-[11px] font-medium transition-colors ${locked ? "opacity-55 border-border bg-background text-muted-foreground cursor-not-allowed" : aiModel === m ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}>
                        {locked && <Lock size={9} />}{(ENGINES[m]?.label || m).split(" ").pop()}
                      </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Errors / gate */}
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 flex items-start gap-2 text-xs text-red-500" data-testid="ext-error">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {error}
              </div>
            )}
            {gate && (
              <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 flex flex-col gap-2" data-testid="ext-gate">
                <p className="text-xs text-foreground">{gate}</p>
                <a href="/app/pricing" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-xs font-semibold">View plans →</a>
              </div>
            )}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2" data-testid="ext-actions">
              <button onClick={runScan} disabled={actionsDisabled} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="ext-scan-btn">
                {busy === "scan" ? <Loader2 size={14} className="animate-spin" /> : <Target size={14} />} Detect &amp; Scan
              </button>
              <button onClick={runOptimize} disabled={actionsDisabled} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-primary text-primary text-xs font-semibold hover:bg-primary/5 disabled:opacity-50 transition-colors" data-testid="ext-optimize-btn">
                {busy === "optimize" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Optimize
              </button>
              <button onClick={runCover} disabled={actionsDisabled} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-border text-foreground text-xs font-semibold hover:bg-muted disabled:opacity-50 transition-colors" data-testid="ext-cover-btn">
                {busy === "cover" ? <Loader2 size={14} className="animate-spin" /> : <FileSignature size={14} />} Cover Letter
              </button>
              <button onClick={runEmail} disabled={actionsDisabled} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-border text-foreground text-xs font-semibold hover:bg-muted disabled:opacity-50 transition-colors" data-testid="ext-email-btn">
                {busy === "email" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Rec. Email
              </button>
            </div>

            {busy && (
              <div data-testid="ext-progress">
                <WaitingUX
                  label={busy === "optimize" ? (STEP_LABEL[jobStep] || "Optimizing your resume…") : (step || "Working…")}
                  variant="compact"
                  progress={jobActive ? jobProgress : null}
                />
              </div>
            )}

            {/* Scan result */}
            {scanView && (
              <div className="p-5 bg-card rounded-xl border border-border shadow-md space-y-4" data-testid="ext-scan-result">
                <div className="flex items-center gap-5">
                  <ScoreRing pct={scanView.pct} />
                  <div className="flex-1 grid grid-cols-3 divide-x divide-border">
                    <div className="flex flex-col items-center px-1">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground">Match</span>
                      <span className="text-lg font-bold" style={{ color: "#10b981" }} data-testid="ext-scan-matched">{scanView.matched}</span>
                    </div>
                    <div className="flex flex-col items-center px-1">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground">Partial</span>
                      <span className="text-lg font-bold" style={{ color: "#f59e0b" }}>{scanView.partial}</span>
                    </div>
                    <div className="flex flex-col items-center px-1">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground">Missing</span>
                      <span className="text-lg font-bold" style={{ color: "#ef4444" }}>{scanView.missing}</span>
                    </div>
                  </div>
                </div>

                {scanView.gaps.length > 0 && (
                  <div className="space-y-2 pt-3 border-t border-border/50">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
                        Skills to add — pick yours ({scanView.gaps.filter((g) => selectedActions.includes(g.id)).length}/{scanView.gaps.length})
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setSelectedActions((prev) => Array.from(new Set([...prev, ...scanView.gaps.map((g) => g.id)])))}
                          className="text-[10px] font-medium text-primary hover:underline" data-testid="ext-skills-all">All</button>
                        <span className="text-muted-foreground/40">·</span>
                        <button onClick={() => setSelectedActions((prev) => prev.filter((id) => !scanView.gaps.some((g) => g.id === id)))}
                          className="text-[10px] font-medium text-muted-foreground hover:underline" data-testid="ext-skills-none">Clear</button>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-snug">Tick only the skills you actually have — we add exactly these, woven into your real experience. Never fabricated.</p>
                    {(showAllSkills ? scanView.gaps : scanView.gaps.slice(0, 4)).map((a) => (
                      <SkillCheck key={a.id} action={a}
                        checked={selectedActions.includes(a.id)} onToggle={() => toggleAction(a.id)}
                        placement={skillPlacements[a.skill]} onPlacement={(v) => setPlacement(a.skill, v)} />
                    ))}
                    {scanView.gaps.length > 4 && (
                      <button onClick={() => setShowAllSkills((v) => !v)} className="w-full py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center justify-center gap-1 transition-colors" data-testid="ext-skills-expand">
                        {showAllSkills ? <><ChevronUp size={12} /> Show less</> : <><ChevronDown size={12} /> +{scanView.gaps.length - 4} more</>}
                      </button>
                    )}
                  </div>
                )}

                <button onClick={runOptimize} disabled={actionsDisabled} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="ext-scan-optimize-btn">
                  <Sparkles size={13} /> Optimize to fix these
                </button>
              </div>
            )}

            {/* Optimize result */}
            {optimized && (() => {
              const optScore = typeof optimized.ats_score === "object"
                ? optimized.ats_score?.overall_score
                : optimized.ats_score;
              return (
              <div className="flex flex-col items-center justify-center p-5 bg-card border border-border rounded-xl space-y-3 text-center" data-testid="ext-optimize-result">
                <span className="w-11 h-11 rounded-full bg-green-500/10 flex items-center justify-center"><Check size={22} className="text-green-500" /></span>
                <div>
                  <p className="text-sm font-semibold text-foreground">Resume optimized</p>
                  {optScore != null ? <p className="text-xs text-muted-foreground mt-0.5">New ATS match: <span className="font-bold text-green-500">{optScore}%</span></p> : null}
                </div>
                <button onClick={() => window.open(`${API}/download/${optimized.id}`, "_blank")}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-lg font-medium text-sm shadow-lg shadow-primary/20 hover:opacity-90 transition-opacity" data-testid="ext-download-btn">
                  <Download size={16} /> Download DOCX
                </button>
              </div>
              );
            })()}

            {/* Cover letter */}
            {cover && (
              <div className="p-4 bg-card border border-border rounded-xl space-y-3" data-testid="ext-cover-result">
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Cover Letter</span>
                <TextPreview text={cover} testid="ext-cover-text" serif />
                <button onClick={downloadCover} disabled={dlBusy}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-white text-black hover:bg-gray-100 rounded-lg font-medium text-sm border border-gray-200 disabled:opacity-60 transition-colors" data-testid="ext-cover-download">
                  {dlBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF
                </button>
              </div>
            )}

            {/* Recruiter email */}
            {emailText && (
              <div className="p-4 bg-card border border-border rounded-xl space-y-3" data-testid="ext-email-result">
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Recruiter Email{detectedEmail ? ` · to ${detectedEmail}` : ""}</span>
                <TextPreview text={emailText} testid="ext-email-text" />
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={copyEmail} className="flex items-center justify-center gap-2 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-xs font-medium transition-colors" data-testid="ext-email-copy">
                    {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
                  </button>
                  <button onClick={openGmail} className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:-translate-y-0.5 shadow-md transition-transform" data-testid="ext-email-gmail">
                    <Rocket size={14} /> Open in Gmail
                  </button>
                </div>
              </div>
            )}

            <a href="/app" target="_blank" rel="noreferrer" className="text-[10px] text-center text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1 w-full pt-1 transition-colors">
              <MapPin size={11} /> Open full dashboard
            </a>
          </>
        )}
      </div>
    </div>
  );
}
